import { origemPedidoSchema } from '@projetive-op/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import {
  BarraTitulo,
  BotaoEditar,
  Formulario,
  RodapeGaveta,
  aoSubmeter,
  useGavetaCadastro,
} from '@/components/cadastros/base'
import { Campo, Checkbox, Input } from '@/components/ui/campo'
import { Gaveta } from '@/components/ui/gaveta'
import { Ativo, Cabecalho, Carregando, Linha, Tabela, Td, Th, Vazio } from '@/components/ui/tabela'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/gestao/origens')({ component: Origens })

interface Item {
  id: string
  nome: string
  ativo: boolean
}

function Origens() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const lista = useQuery(trpc.cadastros.origens.listar.queryOptions())
  const g = useGavetaCadastro<Item>()
  const invalidar = () =>
    qc.invalidateQueries({ queryKey: trpc.cadastros.origens.listar.queryKey() })
  const criar = useMutation(
    trpc.cadastros.origens.criar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )
  const atualizar = useMutation(
    trpc.cadastros.origens.atualizar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )

  const submit = aoSubmeter({
    schema: origemPedidoSchema,
    booleanos: ['ativo'],
    setErros: g.setErros,
    sucesso: (d) => `Origem ${d.nome} salva.`,
    onValido: (dados) =>
      g.item ? atualizar.mutateAsync({ id: g.item.id, ...dados }) : criar.mutateAsync(dados),
  })

  return (
    <>
      <BarraTitulo
        titulo="Origens de pedido"
        descricao="De onde vem a venda: atacado, internet, cooperativa…"
        onNovo={g.abrirNovo}
        rotuloNovo="Nova origem"
      />
      <Tabela>
        <Cabecalho>
          <Th>Nome</Th>
          <Th>Pedidos</Th>
          <Th />
        </Cabecalho>
        <tbody>
          {lista.data?.map((o) => (
            <Linha key={o.id}>
              <Td className="font-medium">
                {o.nome} {!o.ativo && <Ativo ativo={false} />}
              </Td>
              <Td numerico>{o._count.pedidos}</Td>
              <Td className="text-right">
                <BotaoEditar rotulo={o.nome} onClick={() => g.abrirEditar(o)} />
              </Td>
            </Linha>
          ))}
          {lista.isPending && <Carregando colunas={3} />}
          {lista.data?.length === 0 && <Vazio colunas={3}>Nenhuma origem.</Vazio>}
        </tbody>
      </Tabela>
      <Gaveta
        aberta={g.aberta}
        titulo={g.item ? 'Editar origem' : 'Nova origem'}
        onFechar={g.fechar}
        rodape={
          <RodapeGaveta
            form="form-origem"
            salvando={criar.isPending || atualizar.isPending}
            onCancelar={g.fechar}
          />
        }
      >
        <Formulario id="form-origem" onSubmit={submit} erros={g.erros} onLimparErro={g.limparErro}>
          <Campo label="Nome" erro={g.erros.nome}>
            {(p) => <Input {...p} name="nome" defaultValue={g.item?.nome} autoFocus />}
          </Campo>
          <Checkbox name="ativo" label="Ativa" defaultChecked={g.item?.ativo ?? true} />
        </Formulario>
      </Gaveta>
    </>
  )
}
